import { busyStateMessage, useCesiumViewer } from "$lib/core/map/cesium-viewer";
import { CallbackPositionProperty, Cartesian2, Cartesian3, CzmlDataSource, defined, Entity, HorizontalOrigin, IntersectionTests, Math as CesiumMath, Matrix3, Matrix4, Plane, Ray, ScreenSpaceEventType, Transforms, VerticalOrigin, Viewer, ConstantProperty, Quaternion, HeadingPitchRoll } from "cesium";
import { useDataSources } from "$lib/core/map/cesium-datasources";
import { collectGroupEntities, createPreviewEntity, getGroupRoot } from "./utils";
import { mapToolsStore } from "../map-tools-store";
import { toggleEscapeToolListener } from "../map-tools";
import { OriginalCesiumInputActions } from "../cesium-original-input-actions";
import { getEntityPropertyBag } from "../map-utils";
import type { IMastPointMinimalModel } from "$lib/models/IMastPointMinimalModel";
import { toast } from "$lib/components/organisms/toast/toast";
  
// Type for items in tool state
type RotItem = {
    preview: Entity;
    offsetEcef: Cartesian3; // (pos - rootPos) in ECEF
    baseRot: Matrix3; // orientation as a rotation matrix in ECEF
};
// Type for the complete tool state
type RotationToolState = {
    active: boolean;

    previewDatasource: CzmlDataSource | null;
    root: Entity | null;
    members: Entity[];

    rootPos: Cartesian3 | null;
    axisWorld: Cartesian3 | null; // normalized

    rootFrameInv: Matrix4 | null;
    upWorld: Cartesian3 | null;

    items: RotItem[];

    ring: Entity | null;
    handle: Entity | null;
    label: Entity | null;
    radius: number;

    dragging: boolean;
    startAngleRad: number;
    deltaAngleRad: number;
};
// Default (inactive) state
const defaultState: RotationToolState = {
    active: false,
    previewDatasource: null,
    root: null,
    axisWorld: null,
    members: [],
    rootPos: null,
    rootFrameInv: null,
    upWorld: null,
    items: [],
    ring: null,
    handle: null,
    label: null,
    radius: 0,
    dragging: false,
    startAngleRad: 0,
    deltaAngleRad: 0
};
    

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////         ROTATION GIZMO         /////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Create Gizmo ring
 * 
 * @param rootFrame from state
 * @param radius radius (meters)
 * @param segments granularity of ring
 * @returns 
 */
const createRingPositionsENU = (rootFrame: Matrix4, radius: number, segments = 96) => {
    const pts: Cartesian3[] = [];
    for (let i = 0; i <= segments; i++) {
        const a = (i / segments) * (2 * Math.PI);
        const local = new Cartesian3(Math.cos(a) * radius, Math.sin(a) * radius, 0);
        pts.push(Matrix4.multiplyByPoint(rootFrame, local, new Cartesian3()));
    }
    return pts;
}

/**
 * Create rotation gizmo for visualizing and handling rotation. Label, ring (polyline) and handle (point).
 * 
 * @param previewDatasource Datasource to display gizmo in
 * @param rootWorld Mastpoint world position
 * @param rootFrame Mastpoint ENU frame
 * @param radius Radius of ring (meters)
 * @returns 
 */
const createRotationGizmo = (
    previewDatasource: CzmlDataSource,
    rootWorld: Cartesian3,
    rootFrame: Matrix4,
    radius: number
) => {
    const ringPositions = createRingPositionsENU(rootFrame, radius);

    const ring = previewDatasource.entities.add({
        polyline: { positions: ringPositions, width: 3, clampToGround: false }
    });

    // handle at 0 degrees => +East in ENU
    const handleWorld = Matrix4.multiplyByPoint(
        rootFrame,
        new Cartesian3(radius, 0, 0),
        new Cartesian3()
    );

    const handle = previewDatasource.entities.add({
        position: handleWorld,
        point: { 
        pixelSize: 10, 
        outlineWidth: 2,
        disableDepthTestDistance : Number.POSITIVE_INFINITY
        }
    });

    const label = previewDatasource.entities.add({
        position: rootWorld,
        label: {
        font: '18px Poppins, sans-serif',
        text: "0°",
        showBackground: true,
        pixelOffset: new Cartesian2(0, -40),
        horizontalOrigin: HorizontalOrigin.CENTER,
        verticalOrigin: VerticalOrigin.BOTTOM,
        disableDepthTestDistance : Number.POSITIVE_INFINITY

        }
    });

    return { ring, handle, label, radius };
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////////////         UTILITIES         //////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Get base orientation quaternion of entity, or default ENU-aligned if none defined.
 * @param e Entity
 * @param pos Entity position
 * @returns 
 */
const getBaseOrientationQuaternion = (e: Entity, pos: Cartesian3): Quaternion => {
    const q = e.orientation?.getValue();
    if (defined(q)) return q;
    return Transforms.headingPitchRollQuaternion(
        pos,
        new HeadingPitchRoll(0, 0, 0)
    )
}

/**
 * Get the mast axis in world coordinates (normalized). Handles both undefined and defined root orientations, though the latter should not occur.
 * Mastpoints should not have an orientation defined, only poles for the time being. Everything else is relative to poles.
 * @param root 
 * @param rootPos 
 * @returns 
 */
const getMastAxisWorld = (root: Entity, rootPos: Cartesian3) => {
    // Set default ENU up at root
    let axis = Matrix4.multiplyByPointAsVector(
        Transforms.eastNorthUpToFixedFrame(rootPos),
        new Cartesian3(0, 0, 1),
        new Cartesian3()
    );
    Cartesian3.normalize(axis, axis);

    // If root has orientation, use it to define the pole axis. Should not be a case we hit yet.
    if (root.orientation) {
        const qRoot = root.orientation.getValue();
        if (defined(qRoot)) {
        const mRoot = Matrix3.fromQuaternion(qRoot, new Matrix3());
        axis = Matrix3.multiplyByVector(mRoot, new Cartesian3(0, 0, 1), new Cartesian3());
        Cartesian3.normalize(axis, axis);
        }
    }

    return axis;
}

/**
 * Intersects a ray from the camera through the screen position with a plane defined by a point and normal.
 */
const intersectRayWithPlane = (
    viewer: Viewer,
    screenPos: Cartesian2,
    planePoint: Cartesian3,
    planeNormal: Cartesian3
  ): Cartesian3 | undefined  => {
    const ray = viewer.camera.getPickRay(screenPos);
    if (!ray) return undefined;
  
    const plane = Plane.fromPointNormal(planePoint, planeNormal);
    return IntersectionTests.rayPlane(ray, plane) ?? undefined;
}

/**
 * Transforms world point to root ENU frame and computes angle in radians from East axis.
 */
const angleInRootENU = (rootFrameInv: Matrix4, worldPoint: Cartesian3) => {
    const local = Matrix4.multiplyByPoint(rootFrameInv, worldPoint, new Cartesian3());
    return Math.atan2(local.y, local.x); // radians
}

/**
 * Transforms angle to be within [-pi, pi]
 */
const normalizeAngleRad = (a: number): number => {
    // wrap to [-pi, pi]. Signed angle form
    a = (a + Math.PI) % (2 * Math.PI);
    if (a < 0) a += 2 * Math.PI;
    return a - Math.PI;
}  


/**
 * Not really necessary now. The boolean is all that is needed at the moment.
 * Will be expanded with controls more tightly coupled with active rotation in the future. 
 */
const toggleMapInteractions = (viewer: Viewer, enable: boolean) => {
    viewer.scene.screenSpaceCameraController.enableRotate = false;
    viewer.scene.screenSpaceCameraController.enableTilt = false;
    return enable;
}


//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
///////////////////////////////////////////         PREVIEW LOGIC         ////////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/**
 * Builds each entity at runtime as a preview item, with offset and base orientation. The construction maintains a rigid-body relative to root.
 */
const buildRigidRotationItems = (
    root: Entity,
    members: Entity[],
    previewDatasource: CzmlDataSource
): {
    rootPos: Cartesian3;
    axisWorld: Cartesian3; // normalized
    items: RotItem[];
    // For gizmo / drag-plane only:
    rootFrameInv: Matrix4;
    upWorld: Cartesian3;
    // For ring sizing:
    ringRadiusMeters: number;
} | undefined => {

    const rootPos = root.position?.getValue();
    if (!rootPos) return undefined;

    const axisWorld = getMastAxisWorld(root, rootPos, ); // normalized

    // For drag-plane intersection + computing angle in local ENU
    const rootFrame = Transforms.eastNorthUpToFixedFrame(rootPos);
    const rootFrameInv = Matrix4.inverse(rootFrame, new Matrix4());
    const upWorld = Matrix4.multiplyByPointAsVector(
        rootFrame,
        new Cartesian3(0, 0, 1),
        new Cartesian3()
    );
    Cartesian3.normalize(upWorld, upWorld);

    const items: RotItem[] = [];

    // ring radius based on max horizontal distance in ENU (stable UI)
    let maxXY = 0;

    for (const e of members) {
        const pos = e.position?.getValue();
        if (!pos) continue;

        // ECEF rigid-body offset
        const offsetEcef = Cartesian3.subtract(pos, rootPos, new Cartesian3());

        // Base orientation as Matrix3 in world frame
        //const baseRot = getWorldRotationMatrix(e);
        const qBase = getBaseOrientationQuaternion(e, pos);
        const baseRot = Matrix3.fromQuaternion(qBase, new Matrix3());

        // Preview entity
        const preview = createPreviewEntity(e, previewDatasource);
        if(!preview) continue;
        preview.position = new CallbackPositionProperty(() => pos, false);

        items.push({ preview, offsetEcef, baseRot });

        // Compute UI radius using ENU projection of the *current* position
        const local = Matrix4.multiplyByPoint(rootFrameInv, pos, new Cartesian3());
        const d = Math.hypot(local.x, local.y);
        if (d > maxXY) maxXY = d;
    }

    const paddingFactor = 1.15;
    const minRadius = 2.0;
    const ringRadiusMeters = Math.max(minRadius, maxXY * paddingFactor);

    return { rootPos, axisWorld, items, rootFrameInv, upWorld, ringRadiusMeters };
  }

/**
 * Updates each entity preview position and orientation according to rigid rotation around root. Runs on mouse move.
 * 
 * @param rootPos World position of root
 * @param axisWorld Normalized (!) 
 * @param items Array of construction items
 * @param deltaAngleRad Angle delta in radians
 */
const applyRigidRotationPreview =(
    rootPos: Cartesian3,
    axisWorld: Cartesian3, // normalized
    items: RotItem[],
    deltaAngleRad: number
) => {
    // delta rotation: axis-angle -> quaternion -> matrix
    const axis = Cartesian3.normalize(axisWorld, new Cartesian3());
    const deltaQ = Quaternion.fromAxisAngle(axis, deltaAngleRad, new Quaternion()); // :contentReference[oaicite:2]{index=2}
    const deltaR = Matrix3.fromQuaternion(deltaQ, new Matrix3());                   // :contentReference[oaicite:3]{index=3}
  
    for (const it of items) {
      // Position: root + R * offset
      const rotatedOffset = Matrix3.multiplyByVector(deltaR, it.offsetEcef, new Cartesian3());
      const newPos = Cartesian3.add(rootPos, rotatedOffset, new Cartesian3());
      it.preview.position = new CallbackPositionProperty(() => newPos, false);
  
      // Orientation: R * baseRot
      const newRot = Matrix3.multiply(deltaR, it.baseRot, new Matrix3());
      const newQ = Quaternion.fromRotationMatrix(newRot, new Quaternion());
      it.preview.orientation = new ConstantProperty(newQ);
    }
  }

/**
 * Triggered when selecting an entity group. The functions starts building entity previews, gizmo, general state and 
 * applies Cesium viewer rules.
 */
const startRotationPreview = (viewer: Viewer, pickedEntity: Entity, refDatasource: CzmlDataSource) => {
    
    busyStateMessage.set('Forbereder forhåndsvisning...');
    viewer.scene.globe.depthTestAgainstTerrain = false;
    // Get entity group
    const root = getGroupRoot(pickedEntity);
    const members = collectGroupEntities(root, pickedEntity.entityCollection);

    // Include root itself in rotation. Not visible now, but why not. :)
    const all = [root, ...members];

    // Build preview clones + local offsets
    const built = buildRigidRotationItems(root, all, refDatasource);

    if (!built || built.items.length === 0) {
        toast({
            type: 'error',
            description: 'Kunne ikke starte rotasjonsverktøy. Sjekk at mastpunktet og komponentene har gyldige posisjoner.',
            title: 'Feil ved start av rotasjonsverktøy'
        })
        
        busyStateMessage.set('');
        return;
    }

    const rootWorld = built.rootPos;
    const rootFrame = Transforms.eastNorthUpToFixedFrame(rootWorld!);

    // Create Gizmo
    const gizmo = createRotationGizmo(refDatasource, rootWorld, rootFrame, built.ringRadiusMeters);

    busyStateMessage.set('');
    // Store tool state
    return {
        active: true,
        previewDatasource: refDatasource,

        root,
        members: all,

        rootPos: built.rootPos,
        axisWorld: built.axisWorld,

        rootFrameInv: built.rootFrameInv,
        upWorld: built.upWorld,

        items: built.items,
        ring: gizmo.ring,
        handle: gizmo.handle,
        label: gizmo.label,
        radius: gizmo.radius,

        dragging: false,
        startAngleRad: 0,
        deltaAngleRad: 0
    }

}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////         TOOL CONTROLS         ///////////////////////////////////////////////////////
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const activateRotateMastpointTool = (mastEntity?: Entity) => {
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;
    const { temp_referenceDataSource } = useDataSources();

    if(!viewer.dataSources.contains(temp_referenceDataSource)){
        viewer.dataSources.add(temp_referenceDataSource);
    }

    mapToolsStore.disableAllToolsExceptToggleToolByName('rotateMastpoint', viewer);
    toggleEscapeToolListener({toggleOn: true});

    let state: RotationToolState = defaultState;
    if(mastEntity){
        const newState = startRotationPreview(viewer, mastEntity, temp_referenceDataSource);
        if(!newState) return;
        mapToolsStore.update((store) => {
            store.rotateMastpoint.payload.mastId = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(newState.root).data.id;
            return store;
        })
        state = newState;
    }
    
    screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {

        const picked = viewer.scene.pick(event.position);
        const pickedEntity = picked?.id as Entity | undefined;

        if(!state.active){
            if (!picked || !defined(pickedEntity)) return;
            
            const newState = startRotationPreview(viewer, pickedEntity, temp_referenceDataSource);
            if(!newState) return;
            mapToolsStore.update((store) => {
                store.rotateMastpoint.payload.mastId = getEntityPropertyBag<IMastPointMinimalModel.MastPointPropertyBagModel>(newState.root).data.id;
                return store;
            })
            state = newState;
            return;
        }

        /*************************************************
        ** May be used as inspiration for extension, change of behaviour or similar tools in the future. 
        ** E.g. choose rotation origo by entity, or rotate single entity.
        **************************************************
        if(!state.dragging){
            const offsetDeg = CesiumMath.toDegrees(state.deltaAngleRad);
            // Post this value as the relative angle of rotation to the backend here.
            // IF we want left click as the finalizer.
        }
        if (state && state.members){
            //const picked = viewer.scene.pick(event.position);
            //const pickedEntity = picked?.id as Entity | undefined;
            // if(state.members.includes(pickedEntity)){}
            // startRotationPreview -> Needs to support custom pivot entity in the group, not only the parent.
        }*/
    }, ScreenSpaceEventType.LEFT_CLICK);

    screenSpaceEventHandler.setInputAction((event: { position: Cartesian2 }) => {
        
        if (!state.active 
            || !state.handle 
            || !state.rootPos 
            || !state.upWorld 
            || !state.rootFrameInv
        ) return;
        if(state.active){
            state.dragging = toggleMapInteractions(viewer, true);
        } else {
            state.dragging = toggleMapInteractions(viewer, false);
        }

        const picked = viewer.scene.pick(event.position);
        const pickedEntity = picked?.id as Entity | undefined;

        // Only start drag if handle is picked
        if (pickedEntity !== state.handle) return;

        const hit = intersectRayWithPlane(viewer, event.position, state.rootPos, state.upWorld);
        if (!hit) return;

        state.startAngleRad = angleInRootENU(state.rootFrameInv, hit);

    }, ScreenSpaceEventType.LEFT_DOWN);

    screenSpaceEventHandler.setInputAction((_: { position: Cartesian2 }) => {
        if(!state.active) return;
        state.dragging = toggleMapInteractions(viewer, false);
    }, ScreenSpaceEventType.LEFT_UP);

    screenSpaceEventHandler.setInputAction((event: { endPosition: Cartesian2 }) => {
        if (!state.active || !state.dragging) return;
        if (!state.rootPos || !state.upWorld || !state.rootFrameInv || !state.upWorld) return;
        if (!state.handle || !state.label) return;

        const hit = intersectRayWithPlane(viewer, event.endPosition, state.rootPos, state.upWorld);
        if (!hit) return;

        const currentAngle = angleInRootENU(state.rootFrameInv, hit);
        const delta = normalizeAngleRad(state.startAngleRad - currentAngle); // positive = CW, negative = CCW. Flip argument to invert

        // Negative to make positive = CW, negative = CCW
        state.deltaAngleRad = -delta;

        applyRigidRotationPreview(
            state.rootPos!,
            state.axisWorld!,
            state.items,
            state.deltaAngleRad
        );

        // Update gizmo handle + label (UI only, ENU ring)
        const rootFrame = Transforms.eastNorthUpToFixedFrame(state.rootPos!);

        // Make handle follow mouse angle (Positive CW, negative CCW)
        const handleAngle = -delta;
        const handleLocal = new Cartesian3(
            Math.cos(handleAngle) * state.radius,
            Math.sin(handleAngle) * state.radius,
            0
        );
        const handleWorld = Matrix4.multiplyByPoint(rootFrame, handleLocal, new Cartesian3());
        state.handle!.position! = new CallbackPositionProperty( () => handleWorld, false);

        const deg = CesiumMath.toDegrees(delta);
        state.label!.label!.text = new ConstantProperty(`${deg.toFixed(1)}°`);

        mapToolsStore.update((store) => {
            store.rotateMastpoint.payload.bearingOffset = deg;
            return store;
        })


    }, ScreenSpaceEventType.MOUSE_MOVE);
}

const deactivateRotationTool = () => {
    const { temp_referenceDataSource } = useDataSources();
    const viewer = useCesiumViewer();
    const screenSpaceEventHandler = viewer.screenSpaceEventHandler;

    viewer.scene.globe.depthTestAgainstTerrain = true;
    viewer.scene.screenSpaceCameraController.enableRotate = true;
    viewer.scene.screenSpaceCameraController.enableTilt = true;
    
    temp_referenceDataSource.entities.removeAll();
    toggleEscapeToolListener({toggleOn: false});
    mapToolsStore.update((store) => {
        store.rotateMastpoint.toolEnabled = false;
        store.rotateMastpoint.payload.mastId = undefined;
        store.rotateMastpoint.payload.bearingOffset = undefined;
        return store;
    })

    screenSpaceEventHandler.setInputAction(OriginalCesiumInputActions.LEFT_CLICK,ScreenSpaceEventType.LEFT_CLICK);
    screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_DOWN);
    screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
    screenSpaceEventHandler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
}


export { 
    activateRotateMastpointTool,
    deactivateRotationTool
};